import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseIcon from '@mui/icons-material/Close';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import EditIcon from '@mui/icons-material/Edit';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { ReferenceField, TextField } from 'react-admin';
import { DEFAULT_API_ADMIN } from '../../constants';
import { httpClientReactAdmin } from '../../lib';
import { RECEIVABLE_STATUS_LABELS } from '../../models';

/**
 * Conferência do relatório de cobranças com o "Contas a receber" do Gestão Click, pelo vencimento
 * (a competência daqui). O backend lê o portal e devolve os boletos já separados por tipo de
 * divergência; aqui é só apresentação.
 */

const GESTAO_CLICK_URL = id =>
  `https://gestaoclick.com/financeiro/movimentacoes_financeiras/visualizar_recebimento/${id}?retorno=%2Fmovimentacoes_financeiras%2Findex_recebimento`;

const GROUPS = [
  { key: 'mismatched', label: 'Divergentes', color: 'warning', hint: 'O sync copia o Gestão Click: corrija lá e clique em Atualizar Cobranças.' },
  { key: 'onlyGestaoClick', label: 'Só no Gestão Click', color: 'info', hint: 'Ainda não chegaram aqui. Atualizar Cobranças traz.' },
  { key: 'unlinkedClient', label: 'Cliente sem posto', color: 'error', hint: 'Cliente do Gestão Click sem posto vinculado: o sync nunca traz. Vincule na aba Gestão Click do posto.' },
  { key: 'onlyPontuax', label: 'Só no PontuaX', color: 'default', hint: 'Boleto apagado no Gestão Click. O sync cancela na próxima execução.' },
];

const STATUS_LABELS = { ...RECEIVABLE_STATUS_LABELS, canceled: { label: 'Cancelada', color: 'default' } };

const money = value => (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const day = iso => (iso ? moment(iso).format('DD/MM/YYYY') : '—');
const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
// Sem moment: o locale dele é global e vem do idioma do usuário (saía "Oct").
const monthLabel = iso => `${MONTHS[Number(iso.slice(5, 7)) - 1]}/${iso.slice(0, 4)}`;

const newTab = { target: '_blank', rel: 'noopener noreferrer' };

const cardPadding = { p: { xs: 1.5, md: 2 } };
const bigNumber = { fontSize: { xs: '1.25rem', md: '1.5rem' } };

const SummaryCard = ({ title, totals, noun }) => (
  <Box sx={{ ...cardPadding, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
    <Typography variant="overline" color="text.secondary">{title}</Typography>
    <Typography variant="h5" fontWeight={700} sx={bigNumber}>{money(totals.value)}</Typography>
    <Typography variant="body2" color="text.secondary">{totals.count} {noun}</Typography>
    <Stack direction="row" spacing={1} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
      <Chip size="small" color="success" variant="outlined" label={`Pago ${money(totals.paidValue)}`} />
      <Chip size="small" variant="outlined" label={`Em aberto ${money(totals.openValue)}`} />
    </Stack>
  </Box>
);

const DifferenceCard = ({ value, issues }) => {
  const ok = Math.abs(value) < 0.01 && issues === 0;
  const color = ok ? 'success' : 'warning';
  return (
    <Box sx={theme => ({
      ...cardPadding, borderRadius: 2, border: 1,
      borderColor: alpha(theme.palette[color].main, 0.5),
      bgcolor: alpha(theme.palette[color].main, 0.08),
    })}>
      <Typography variant="overline" color={`${color}.main`}>Diferença</Typography>
      <Typography variant="h5" fontWeight={700} color={`${color}.main`} sx={bigNumber}>{money(value)}</Typography>
      <Typography variant="body2" color="text.secondary">
        {issues === 0 ? 'Nenhum boleto divergente' : `${issues} ${issues === 1 ? 'boleto para olhar' : 'boletos para olhar'}`}
      </Typography>
    </Box>
  );
};

const StatusChip = ({ status }) => {
  const badge = STATUS_LABELS[status];
  return badge ? <Chip size="small" color={badge.color} label={badge.label} variant={status === 'canceled' ? 'outlined' : 'filled'} /> : null;
};

/** Célula de um lado da comparação. Destacada quando é o campo que diverge. */
const SideCell = ({ children, highlight, empty }) => (
  <TableCell
    align="center"
    sx={theme => ({
      whiteSpace: 'nowrap',
      ...(highlight && { bgcolor: alpha(theme.palette.warning.main, 0.14), fontWeight: 700 }),
      ...(empty && { color: 'text.disabled' }),
    })}
  >
    {empty ? '—' : children}
  </TableCell>
);

const PlaceCell = ({ item }) => {
  if (!item.placeId) {
    return (
      <Box>
        <Typography variant="body2" fontWeight={600}>{item.client?.name}</Typography>
        <Typography variant="caption" color="text.secondary">{item.client?.document || 'sem CNPJ'}</Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography
        component="a"
        href={`#/stations/${item.placeId}/show`}
        {...newTab}
        variant="body2"
        fontWeight={600}
        sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
      >
        <ReferenceField record={item} source="placeId" reference="stations" link={false}>
          <TextField source="name" />
        </ReferenceField>
      </Typography>
      {item.chainId && (
        <Typography variant="caption" color="text.secondary" component="div">
          <ReferenceField record={item} source="chainId" reference="chains" link={false}>
            <TextField source="name" variant="caption" />
          </ReferenceField>
        </Typography>
      )}
    </Box>
  );
};

const CompareTable = ({ items }) => {
  const differs = (item, field) => item.diffs.includes(field);
  const groupHeader = { fontWeight: 700, borderBottom: 0, pb: 0 };
  return (
    <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, maxHeight: '55vh' }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell rowSpan={2} sx={{ fontWeight: 700, minWidth: 220 }}>Posto</TableCell>
            <TableCell colSpan={3} align="center" sx={groupHeader}>Gestão Click</TableCell>
            <TableCell colSpan={3} align="center" sx={groupHeader}>PontuaX</TableCell>
            <TableCell rowSpan={2} />
          </TableRow>
          <TableRow>
            {['Vencimento', 'Valor', 'Status', 'Vencimento', 'Valor', 'Status'].map((label, i) => (
              <TableCell key={i} align="center" sx={{ top: 33, color: 'text.secondary' }}>{label}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => {
            const gc = item.gestaoClick;
            const px = item.pontuax;
            return (
              <TableRow key={item.gestaoClickId || item.billId} hover>
                <TableCell><PlaceCell item={item} /></TableCell>
                <SideCell empty={!gc} highlight={differs(item, 'dueDate')}>{day(gc?.dueDate)}</SideCell>
                <SideCell empty={!gc} highlight={differs(item, 'value')}>{money(gc?.value)}</SideCell>
                <SideCell empty={!gc} highlight={differs(item, 'status')}><StatusChip status={gc?.status} /></SideCell>
                <SideCell empty={!px} highlight={differs(item, 'dueDate')}>{day(px?.dueDate)}</SideCell>
                <SideCell empty={!px} highlight={differs(item, 'value')}>{money(px?.value)}</SideCell>
                <SideCell empty={!px} highlight={differs(item, 'status')}><StatusChip status={px?.status} /></SideCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {item.gestaoClickId && (
                    <Tooltip title="Abrir no Gestão Click">
                      <IconButton size="small" component="a" href={GESTAO_CLICK_URL(item.gestaoClickId)} {...newTab}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {item.billId && (
                    <Tooltip title="Editar cobrança">
                      <IconButton size="small" component="a" href={`#/client-bills/${item.billId}`} {...newTab}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const Result = ({ data }) => {
  const groups = GROUPS.filter(group => data[group.key]?.length);
  const issues = groups.reduce((sum, group) => sum + data[group.key].length, 0);
  const [tab, setTab] = useState(groups[0]?.key);
  const current = groups.find(group => group.key === tab) || groups[0];

  return (
    <Stack spacing={2.5}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' } }}>
        <SummaryCard title="Gestão Click" totals={data.totals.gestaoClick} noun="boletos" />
        <SummaryCard title="PontuaX" totals={data.totals.pontuax} noun="cobranças" />
        <DifferenceCard value={data.totals.gestaoClick.value - data.totals.pontuax.value} issues={issues} />
      </Box>

      {issues === 0 ? (
        <Stack alignItems="center" spacing={1} sx={{ py: 4 }}>
          <CheckCircleRoundedIcon color="success" sx={{ fontSize: 56 }} />
          <Typography variant="h6">Tudo bate com o Gestão Click</Typography>
        </Stack>
      ) : (
        <Box>
          <Tabs value={current.key} onChange={(_, value) => setTab(value)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 1.5 }}>
            {groups.map(group => (
              <Tab
                key={group.key}
                value={group.key}
                sx={{ textTransform: 'none', fontWeight: 600 }}
                label={(
                  <Stack direction="row" spacing={1} alignItems="center">
                    <span>{group.label}</span>
                    <Chip size="small" color={group.color} label={data[group.key].length} />
                  </Stack>
                )}
              />
            ))}
          </Tabs>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{current.hint}</Typography>
          <CompareTable items={data[current.key]} />
        </Box>
      )}
    </Stack>
  );
};

const GestaoClickCompareDialog = ({ open, onClose, startMonth, endMonth }) => {
  const [state, setState] = useState({ loading: true });
  const from = moment(startMonth).startOf('month').format('YYYY-MM-DD');
  const to = moment(endMonth || startMonth).endOf('month').format('YYYY-MM-DD');

  const load = useCallback(async () => {
    setState({ loading: true });
    try {
      const { json } = await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/gestao-click/receivables-compare?from=${from}&to=${to}`);
      setState({ data: json });
    } catch (error) {
      setState({ data: { status: 'unavailable' } });
    }
  }, [from, to]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const period = from.slice(0, 7) === to.slice(0, 7) ? monthLabel(from) : `${monthLabel(from)} a ${monthLabel(to)}`;
  const { loading, data } = state;
  const phone = useMediaQuery(theme => theme.breakpoints.down('sm'));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={phone}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 7 }}>
        <CompareArrowsIcon color="primary" />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="div">Conferência com o Gestão Click</Typography>
          <Typography variant="body2" color="text.secondary">Vencimento em {period} · todos os postos</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
            <CircularProgress />
            <Typography color="text.secondary">Lendo o relatório do Gestão Click…</Typography>
          </Stack>
        )}
        {!loading && data?.status === 'ok' && <Result data={data} />}
        {!loading && data?.status === 'token_expired' && (
          <Alert severity="warning">
            A sessão do Gestão Click expirou. Renove o token <b>GESTAO_CLICK_PORTAL_TOKEN</b> e faça o deploy do backend.
          </Alert>
        )}
        {!loading && data?.status === 'unavailable' && (
          <Alert severity="error" action={<Button color="inherit" size="small" onClick={load}>Tentar de novo</Button>}>
            O Gestão Click não respondeu.
          </Alert>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GestaoClickCompareDialog;
